import { isStringArray } from '@/shared/utils/typeGuards'

/**
 * The key used to identify the clipboard data that contains the selected
 * table range. This is used so that we can preserve the "dimensions" of a range
 * when pasting into the table, and don't treat all newlines as new rows.
 */
const GO_TABLE_COPY_KEY = 'go/table-selection'

/** Sets clipboard data when copying a range of cells from the table. */
export const setTableClipboardData = (clipboardData: DataTransfer, data: string[][]) => {
  clipboardData.setData(GO_TABLE_COPY_KEY, JSON.stringify(data))
}

/**
 * Used when pasting data into the Go table. Will return a matrix of
 * strings representing the values to be pasted into each cell.
 */
export const getTableClipboardData = (event: ClipboardEvent): string[][] | null => {
  if (!(event instanceof ClipboardEvent) || !event.clipboardData) {
    return null
  }

  const text = event.clipboardData?.getData('text/plain')
  const plaintextMatrix = text?.split('\n').map((line) => line.split('\t'))

  const dataFromGoTableCopy = event.clipboardData.getData(GO_TABLE_COPY_KEY)

  if (!dataFromGoTableCopy) {
    return plaintextMatrix
  }

  try {
    const parsedData = JSON.parse(dataFromGoTableCopy)
    if (isArrayOfStringArrays(parsedData)) {
      return parsedData
    }

    return null
  } catch {
    return plaintextMatrix
  }
}

/** Returns true if the input is an array of arrays of strings. */
function isArrayOfStringArrays(input: unknown): input is string[][] {
  if (!Array.isArray(input)) {
    return false
  }

  return input.every(isStringArray)
}
